import { StrictLocalMsg } from "@faire/localization/react/StrictLocalMsg";
import { tsv } from "@faire/slate/lib/tsv";
import { IPolimorphicProps } from "@faire/slate/private/shared/types";
import React from "react";

type IFormLabelProps<E extends React.ElementType> = IProps<
  Omit<IPolimorphicProps<E, React.LabelHTMLAttributes<E>>, "label">
> & {
  tooltip?: React.ReactNode;
  optional: boolean;
};

export const FormLabel: <E extends React.ElementType>(
  props: IFormLabelProps<E>
) => React.ReactElement | null = ({
  children,
  className,
  disabled,
  error,
  gap = 0,
  tooltip,
  optional,
  as: Component = "label",
  ...props
}) => {
  const labelCls = labelStyle.cls({ className });
  const labelVars = labelStyle.vars({
    gap: `calc(var(--spacing) * ${gap})`,
  });

  if (!children) {
    return null;
  }

  return (
    <Component
      style={{ ...labelVars }}
      className={labelCls}
      data-disabled={disabled}
      data-error={!!error}
      {...props}
    >
      <span>
        {optional ? (
          <StrictLocalMsg
            id="9rQTJ0"
            defaultMessage="{formLabelFullyTranslatedSentence} <SubduedFormatting>(optional)</SubduedFormatting>"
            values={{
              formLabelFullyTranslatedSentence: <span>{children}</span>,
              SubduedFormatting,
            }}
            description={{
              text: "'(optional)' form label suffix: subdued text at the end of an input's label denoting that the input is optional",
              img: "https://cdn.faire.com/fastly/7fbddf072e4ab23243e94d7a239025328835c3ccf30fd639ae38aa2fb73a92dd.png",
            }}
          />
        ) : (
          children
        )}
      </span>
      {tooltip ? (
        <span className="inline-flex pl-(--slate-spacing-element-default) align-text-bottom">
          {tooltip}
        </span>
      ) : null}
    </Component>
  );
};

const labelStyle = tsv({
  vars: {
    gap: "fs-form-field-label-gap",
  },
  base: ["fs-form-field__label", "mb-(--fs-form-field-label-gap)"],
});

type IFormHelperProps = IProps<React.HTMLAttributes<HTMLSpanElement>>;

export const FormHelper: React.FC<IFormHelperProps> = ({
  children,
  className,
  disabled,
  error,
  gap = 1,
  ...props
}) => {
  if (!children && typeof error !== "string") {
    return null;
  }

  const helperCls = helperStyle.cls({ className });
  const helperVars = helperStyle.vars({
    gap: `calc(var(--spacing) * ${gap})`,
  });

  return (
    <span
      style={{ ...helperVars }}
      className={helperCls}
      role={error ? "alert" : undefined}
      data-disabled={disabled}
      data-error={!!error}
      {...props}
    >
      {error === undefined || typeof error === "boolean" ? children : error}
    </span>
  );
};

const SubduedFormatting = (chunks: React.ReactNode) => (
  <span className="text-fs-text-subdued">{chunks}</span>
);

const helperStyle = tsv({
  vars: {
    gap: "fs-form-field-helper-gap",
  },
  base: ["fs-form-field__helper", "mt-(--fs-form-field-helper-gap)"],
});

type IProps<T> = {
  disabled?: boolean;
  error?: React.ReactNode;
  gap?: number;
} & T;
